import { useLazyQuery } from "@apollo/client";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  Divider,
  FlatListSearchable,
  Page,
  Text,
  FlatActionList,
  useWithAppState,
} from "@towersystems/roam-common-native";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import {
  SerialNumber,
  SerialNumberList,
} from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { View } from "react-native-ui-lib";
import {
  LoadingScreen,
  SearchButton,
  SuspendAwait,
} from "../../../../components";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { SerialNumberRootParamList } from "../../../../types";
import SerialNumberModel from "../../../../utilities/data/model/SerialNumber";
import { useProductAwaitState } from "../../../../utilities/products/hooks";
import { useScheduledSearch } from "../../../../utilities/search";
import { useOfflineSerialNumberForProductSearch } from "../../../../utilities/serial-number";
import { SERIAL_NUMBERS_FOR_PRODUCT } from "../../../../utilities/serial-number/constants";
import { toTitleCase } from "../../../../utilities/to-title-case";
import { useAddItem } from "../../utils/sale-controller";
import { DEFAULT_QUERY_OPTIONS, NO_RESULTS_LABEL } from "./constants";
import { SearchSerialNumbersForProductOptions } from "./types";

export type ListProductSerialNumbersScreenProps = StackScreenProps<
  SerialNumberRootParamList,
  "ListProductSerialNumbersScreen"
>;

export const ListProductSerialNumbersScreen = ({
  navigation,
  route,
}: ListProductSerialNumbersScreenProps) => {
  const productId = route.params.productId;
  const { appState } = useWithAppState();

  const productState = useProductAwaitState(productId);

  const online = useOnline();

  const addItem = useAddItem({ navigation });

  const { query, setQuery, executedQuery } = useScheduledSearch();

  const productLocationInventoryPricingId = useMemo(() => {
    if (!productState?.data) {
      return undefined;
    }

    const lip = productState.data.locationInventoryPricing.find(
      (lip) => lip.locationId === appState.locationId
    );

    return lip?.id;
  }, [productState]);

  const [searchSerialNumbersForProduct, { data, loading: onlineLoading }] =
    useLazyQuery<
      { serialNumbersForProduct: SerialNumberList },
      SearchSerialNumbersForProductOptions
    >(SERIAL_NUMBERS_FOR_PRODUCT);

  const onlineItems = data?.serialNumbersForProduct.items || [];

  const {
    data: offlineItems,
    loading: offlineLoading,
    searchSerialNumbersForProduct: searchOfflineSerialNumbersForProduct,
  } = useOfflineSerialNumberForProductSearch(productId);

  const handleRenderItem = useCallback(
    ({ item }: { item: SerialNumber | SerialNumberModel }) => {
      return (
        <FlatActionList.RenderItem
          item={{
            key: item.id,
            title: toTitleCase(item.value),
            onPress: () => {
              addItem(productId, {
                serialNumber: item.value,
                skipVariantPicker: true,
              })
                .then(() => {
                  navigation.getParent()?.goBack();
                })
                .catch(console.log);
            },
          }}
        />
      );
    },
    [addItem, navigation]
  );

  useLayoutEffect(() => {
    if (!online) {
      searchOfflineSerialNumbersForProduct(executedQuery);
    } else {
      if (!productLocationInventoryPricingId) return;
      searchSerialNumbersForProduct({
        variables: {
          productLocationInventoryPricingId: productLocationInventoryPricingId,
          options: {
            ...DEFAULT_QUERY_OPTIONS,
            filter: {
              value: {
                contains: executedQuery,
              },
            },
          },
        },
      });
    }
  }, [online, executedQuery, productLocationInventoryPricingId]);

  const isFauxLoading = executedQuery !== query;

  const normalizedData = online
    ? {
        items: onlineItems.map(
          (item: SerialNumber | SerialNumberModel) => item
        ),
        loading: isFauxLoading || onlineLoading,
      }
    : { items: offlineItems, loading: isFauxLoading || offlineLoading };

  function openCreateSerialNumber(defaultSerialNumber?: string) {
    navigation.navigate("AddSerialNumberProductScreen", {
      productId,
      defaultSerialNumber,
    });
  }

  useEffect(() => {
    navigation.setOptions({
      headerRight: ({}) => (
        <Button
          isAction
          size="compact"
          label="New Serial Number"
          onPress={() => openCreateSerialNumber()}
        ></Button>
      ),
    });
  }, [productId]);

  const loading = normalizedData.loading || isFauxLoading;

  const items = loading ? [] : normalizedData.items;

  const emptyComponent =
    executedQuery && !loading ? (
      <Text>
        {executedQuery
          ? `${NO_RESULTS_LABEL} containing '${executedQuery}'`
          : NO_RESULTS_LABEL}
      </Text>
    ) : undefined;

  const addSerialNumberMarkup =
    !!query && query.length >= 1 ? (
      <View>
        <SearchButton
          title={`Add Non Matched "${query}" Serial Number`}
          secondaryTitle="Serial Number not found in system, press here to add to order."
          onPress={() => openCreateSerialNumber(query)}
        />
        <Divider />
      </View>
    ) : undefined;

  if (!productState) return null;

  return (
    <Page titleContainerStyle={{ alignItems: "center" }}>
      <SuspendAwait resolve={productState} fallback={<LoadingScreen />}>
        {() => {
          return (
            <View>
              <FlatListSearchable
                fullHeight={false}
                placeholder="Search..."
                onChange={(query) => setQuery(query)}
                value={query}
                ItemSeparatorComponent={Divider}
                renderItem={handleRenderItem}
                refreshing={loading}
                data={items}
                scrollEnabled={false}
                ListEmptyComponent={emptyComponent}
                renderSearchBarHeader={(searchBarMarkup, divider) => (
                  <View>
                    <View>
                      {searchBarMarkup}
                      {divider}
                    </View>
                    {addSerialNumberMarkup}
                  </View>
                )}
              />
              {loading ? (
                <FlatListSearchableSkeleton searchbar={false} />
              ) : null}
            </View>
          );
        }}
      </SuspendAwait>
    </Page>
  );
};
